import type { SolverStrategy, StrategyInput, StrategyOutput } from '../types';
import type { TileStackModel } from '../../boardMVC/models/board/TileStackModel';
import { TileColor } from '../../boardMVC/TileColor';
import MathUtils from '../../../utils/MathUtils';
import { adjacentMatchBlockerTypes } from '../../boardMVC/models/board/MatchSequencerMCV';
import { StrategyMakeMatch } from './StrategyMakeMatch';

export class StrategyPrioritizeColors implements SolverStrategy {
  public readonly label = 'prioritize';

  private readonly randomStrategyFallback = new StrategyMakeMatch();

  public getNextMove(input: StrategyInput): StrategyOutput {
    const { boardModel, trayModel } = input;

    const trayItems = trayModel.getItems();
    const topColors = trayModel.getTopColors();

    // Get all empty stacks
    const emptyStacks = boardModel.getEmptyStacks();

    if (emptyStacks.length === 0) {
      return this.randomStrategyFallback.getNextMove(input);
    }

    // Evaluate all possible moves with composite scoring
    const scoredMoves = this.evaluateAllMoves(emptyStacks, trayItems, topColors, boardModel);

    if (scoredMoves.length === 0) {
      return this.randomStrategyFallback.getNextMove(input);
    }

    // Select the best move based on composite score
    const bestMove = this.selectBestScoredMove(scoredMoves);

    return {
      fromStack: bestMove.fromStackIndex,
      toStack: { x: bestMove.stack.x, y: bestMove.stack.y },
    };
  }

  private evaluateAllMoves(
    emptyStacks: TileStackModel[],
    trayItems: TileStackModel[],
    _topColors: TileColor[],
    boardModel: { getStacks: () => TileStackModel[]; cols: number; rows: number },
  ): {
    stack: TileStackModel;
    fromStackIndex: number;
    compositeScore: number;
    priorityBreakdown: { topColor: number; secondaryColor: number; strategic: number; blockerScore: number };
  }[] {
    const scoredMoves: {
      stack: TileStackModel;
      fromStackIndex: number;
      compositeScore: number;
      priorityBreakdown: { topColor: number; secondaryColor: number; strategic: number; blockerScore: number };
    }[] = [];

    // Evaluate each possible move (tray item to empty stack)
    trayItems.forEach((trayItem, trayIndex) => {
      if (!trayItem.hasTiles()) return;

      emptyStacks.forEach((emptyStack) => {
        let blockerScore = 0;
        let topColorScore = 0;
        let secondaryColorScore = 0;
        let strategicScore = 0;

        // Priority 1: Top color matching
        const topColor = trayItem.getTopmostColor();
        if (topColor !== TileColor.None) {
          const connections = emptyStack.getMatchingConnections(topColor, (s) => s.isBlocked());
          topColorScore = connections.length * 100; // High weight for top color matches

          // Blocker priority
          const neighbouringBlockers = emptyStack
            .getConnectedStacks()
            .filter(
              (neighbour) => neighbour.blocker && adjacentMatchBlockerTypes.includes(neighbour.blocker?.getType()),
            );
          blockerScore = neighbouringBlockers.length * topColorScore;
        }

        // Priority 2: Secondary color matching
        if (trayItem.getTileCount() >= 2) {
          const secondaryColor = trayItem.getNextCoveredColor();
          if (secondaryColor !== TileColor.None) {
            const connections = emptyStack.getMatchingConnections(secondaryColor, (s) => s.isBlocked());
            secondaryColorScore = connections.length * 50; // Medium weight for secondary color matches
          }
        }

        // Priority 3: Strategic placement
        strategicScore = this.calculateStrategicScore(emptyStack, boardModel);

        const compositeScore = blockerScore + topColorScore + secondaryColorScore + strategicScore;

        scoredMoves.push({
          stack: emptyStack,
          fromStackIndex: trayIndex,
          compositeScore,
          priorityBreakdown: {
            topColor: topColorScore,
            secondaryColor: secondaryColorScore,
            strategic: strategicScore,
            blockerScore,
          },
        });
      });
    });

    return scoredMoves;
  }

  private calculateStrategicScore(
    emptyStack: TileStackModel,
    boardModel: { getStacks: () => TileStackModel[]; cols: number; rows: number },
  ): number {
    let score = 0;
    let filledNeighborCount = 0;
    let emptyNeighborCount = 0;

    // Check connected stacks (neighbors)
    const connectedStacks = emptyStack.getConnectedStacks();
    connectedStacks.forEach((neighbor) => {
      if (neighbor.hasTiles() && !neighbor.isBlocked()) {
        filledNeighborCount += 1;
        score += 10; // Bonus for being near filled stacks
      } else if (neighbor.isEmpty()) {
        emptyNeighborCount += 1;
        score += 5; // Bonus for having empty neighbors (space to expand)
      }
    });

    // Bonus for having a good balance of filled and empty neighbors
    if (filledNeighborCount > 0 && emptyNeighborCount > 0) {
      score += 20; // High bonus for strategic placement
    }

    // Bonus for being near the center of the board (assuming center is more strategic)
    const centerX = boardModel.cols / 2;
    const centerY = boardModel.rows / 2;
    const distanceFromCenter = Math.sqrt((emptyStack.x - centerX) ** 2 + (emptyStack.y - centerY) ** 2);
    const maxDistance = Math.sqrt(centerX ** 2 + centerY ** 2);
    const centerBonus = Math.max(0, 10 * (1 - distanceFromCenter / maxDistance));
    score += centerBonus;

    return score;
  }

  private selectBestScoredMove(
    scoredMoves: {
      stack: TileStackModel;
      fromStackIndex: number;
      compositeScore: number;
      priorityBreakdown: { topColor: number; secondaryColor: number; strategic: number };
    }[],
  ): {
    stack: TileStackModel;
    fromStackIndex: number;
    compositeScore: number;
    priorityBreakdown: { topColor: number; secondaryColor: number; strategic: number };
  } {
    // Sort by composite score (highest first)
    scoredMoves.sort((a, b) => b.compositeScore - a.compositeScore);

    // Get the highest score
    const maxScore = scoredMoves[0].compositeScore;

    // Filter moves with the highest score
    const bestMoves = scoredMoves.filter((move) => move.compositeScore === maxScore);

    // Randomize among equally good moves
    return MathUtils.rnd.pick(bestMoves);
  }
}
